/** Tiny helpers shared by the data hooks. */

export const EMPTY = [];
export const sum = (rows, pick) => rows.reduce((t, r) => t + (Number(pick(r)) || 0), 0);
export const byDesc = (pick) => (a, b) => (Number(pick(b)) || 0) - (Number(pick(a)) || 0);

/**
 * Re-read a `getPortfolio` document past the backend's own cache — for the
 * moment after a portfolio's analysis finishes, when a plain refetch would be
 * answered with the figures from before it (the backend keeps this query for
 * 24 hours). Only ever for that moment: asked on every load it would switch
 * the cache off.
 *
 * Not `refetch({ forceRefresh: true })`. `forceRefresh` is a field argument,
 * so that answer would be cached under a different key from the one every
 * other read of the portfolio uses, and the next screen to ask would be handed
 * the stale copy. The forced answer is fetched on its own and written back
 * under the variables on screen instead.
 *
 * The document must take `$forceRefresh`. An answer with no portfolio in it
 * is a refusal, not news — writing it would blank a screen that is showing a
 * perfectly good one.
 */
export async function rereadPastBackendCache(client, { query, variables, context }) {
  const { data } = await client.query({
    query,
    variables: { ...variables, forceRefresh: true },
    context,
    fetchPolicy: 'no-cache',
  });
  if (data?.getPortfolio?.portfolio) client.writeQuery({ query, variables, data });
  return data?.getPortfolio ?? null;
}

/** Whether a document declares `$forceRefresh` — rereadPastBackendCache needs it. */
const takesForceRefresh = (doc) => (doc?.definitions ?? []).some((d) => (d.variableDefinitions ?? [])
  .some((v) => v.variable?.name?.value === 'forceRefresh'));

/**
 * Every `getPortfolio` read on screen for one symbol, re-read past the
 * backend's cache — for a component that knows the portfolio changed but not
 * which documents, with which variables, the screen around it read it through.
 *
 * Only ACTIVE queries, and only those whose document takes `$forceRefresh`
 * and names this symbol: anything else is either not on screen or not a read
 * of this portfolio. A screen with none (a fund page) is a no-op.
 */
export async function rereadPortfolioOnScreen(client, securitySymbol) {
  if (!client || !securitySymbol) return [];
  const reads = [...client.getObservableQueries('active').values()].filter((oq) => {
    const v = oq.variables ?? {};
    return takesForceRefresh(oq.query) && (v.securitySymbol === securitySymbol || v.symbol === securitySymbol);
  });
  return Promise.all(reads.map((oq) => rereadPastBackendCache(client, {
    query: oq.query,
    variables: oq.variables,
    context: oq.options?.context,
  }).catch(() => null)));
}

/**
 * The portfolio types that are templates rather than someone's money.
 *
 * Stored lower-case, which is why the old `portfolioType !== 'MODEL'` test
 * never fired: the enum is upper-case but the data is not.
 */
const MODEL_TYPES = new Set(['model', 'model_portfolio', 'asset_model', 'asset_model_variant']);

/**
 * Client money, as opposed to the firm's models.
 *
 * `isModel` alone is not enough — five portfolios carry `portfolioType: "model"`
 * with `isModel: false` (model.300.v0, policy.CapitalTarget.aggressive.7y and
 * friends), so trusting the flag put templates into the book's AUM, client
 * count and risk buckets. The type is checked too, and either one disqualifies.
 */
export const isClientBook = (p) =>
  !p.isModel && !MODEL_TYPES.has(String(p.portfolioType || '').toLowerCase());

/**
 * How many portfolio reads may be in flight at once.
 *
 * `getPortfolio` is not a cheap read: the resolver recomputes behavioural
 * scores for every leaf holding on every call, gated on its own
 * `with_benchmark` default rather than on what the query selected — so even a
 * document asking for nothing but `goalData` pays for the walk. Each one holds
 * a DB session while it does. The server's pool is 20 connections plus 20
 * overflow, so a book read that fired forty-one at once emptied the pool and
 * every later request — the agent's included — waited the full 60 s timeout
 * and then failed with `QueuePool limit ... reached`.
 *
 * Five is below the pool's floor with room to spare for everything else the
 * screen is asking for. It is not much slower in practice either: the reads
 * were never really parallel once the pool was full, they were queued inside
 * the server where nothing could see them.
 */
export const READ_CONCURRENCY = 5;

/**
 * `Promise.all(items.map(fn))` with a ceiling on how many run at once.
 *
 * Resolves to the results in the order of `items`, exactly as `Promise.all`
 * does, so it is a drop-in replacement. `fn` is expected to swallow its own
 * failures (the callers here resolve to null for a row that could not be
 * read); one that throws rejects the whole call, again like `Promise.all`.
 */
export async function mapLimited(items, fn, limit = READ_CONCURRENCY) {
  const list = Array.from(items);
  const out = new Array(list.length);
  let next = 0;

  const worker = async () => {
    for (;;) {
      const i = next;
      next += 1;
      if (i >= list.length) return;
      // eslint-disable-next-line no-await-in-loop -- one worker, one at a time: that is the point.
      out[i] = await fn(list[i], i);
    }
  };

  await Promise.all(Array.from({ length: Math.min(limit, list.length) }, worker));
  return out;
}
